import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Download, RefreshCw, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUTS = [
  { code: "", nom: "Tous" },
  { code: "pending", nom: "En attente" },
  { code: "paid", nom: "Payés" },
  { code: "failed", nom: "Échoués" },
];
const fcfa = (m) => `${(m || 0).toLocaleString("fr-FR")} F`;

/** Bastion : suivi des cadeaux (filtres, export CSV, marquage « Reversé »). */
export const CadeauxAdmin = ({ adminId }) => {
  const [filtres, setFiltres] = useState({ status: "", listing_id: "", depuis: "", jusqu: "" });
  const [donnees, setDonnees] = useState({ cadeaux: [], total: 0 });

  const params = useCallback(
    () => ({ admin_id: adminId, ...Object.fromEntries(Object.entries(filtres).filter(([, v]) => v)) }),
    [adminId, filtres],
  );

  const charger = useCallback(() => {
    axios
      .get(`${API}/admin/cadeaux`, { params: params() })
      .then((r) => setDonnees(r.data))
      .catch(() => toast.error("Impossible de charger les cadeaux"));
  }, [params]);

  useEffect(charger, [charger]);

  const reverser = async (id) => {
    try {
      await axios.post(`${API}/admin/cadeaux/${id}/reverse`, null, { params: { admin_id: adminId } });
      toast.success("Cadeau marqué comme reversé");
      charger();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action impossible");
    }
  };

  const exporter = () => {
    const query = new URLSearchParams(params()).toString();
    window.open(`${API}/admin/cadeaux/export.csv?${query}`, "_blank", "noopener");
  };

  const maj = (cle) => (e) => setFiltres((f) => ({ ...f, [cle]: e.target.value }));
  const champ = "rounded-xl border border-[#E5DFD5] px-3 py-1.5 text-xs text-stone-700 outline-none focus:border-stone-400";

  return (
    <section className="mt-10 rounded-2xl border border-[#E5DFD5] bg-white p-6" data-testid="admin-cadeaux">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading inline-flex items-center gap-2 text-base font-bold text-stone-900 md:text-lg">
          <Gift size={17} className="text-[#E05638]" /> Cadeaux
          <span className="rounded-full bg-[#E05638]/10 px-2.5 py-0.5 text-xs font-bold text-[#E05638]"
            data-testid="admin-cadeaux-total">{donnees.total}</span>
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={charger} data-testid="admin-cadeaux-rafraichir"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DFD5] px-3.5 py-1.5 text-xs font-semibold text-stone-600 hover:border-stone-400">
            <RefreshCw size={13} /> Rafraîchir
          </button>
          <button onClick={exporter} data-testid="admin-cadeaux-export"
            className="inline-flex items-center gap-1.5 rounded-full bg-stone-900 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-stone-700">
            <Download size={13} /> Export CSV
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select value={filtres.status} onChange={maj("status")} data-testid="admin-cadeaux-statut" className={champ}>
          {STATUTS.map((s) => <option key={s.code} value={s.code}>{s.nom}</option>)}
        </select>
        <input value={filtres.listing_id} onChange={maj("listing_id")} placeholder="ID annonce"
          data-testid="admin-cadeaux-annonce" className={champ} />
        <input type="date" value={filtres.depuis} onChange={maj("depuis")}
          data-testid="admin-cadeaux-depuis" className={champ} />
        <input type="date" value={filtres.jusqu} onChange={maj("jusqu")}
          data-testid="admin-cadeaux-jusqu" className={champ} />
      </div>

      <div className="mt-4 flex flex-wrap gap-4 text-xs text-stone-500">
        <span>Encaissé : <strong className="text-stone-800">{fcfa(donnees.total_encaisse)}</strong></span>
        <span>Commission ({donnees.commission_pourcent}%) : <strong className="text-stone-800">{fcfa(donnees.total_commission)}</strong></span>
        <span>À reverser : <strong className="text-stone-800">{fcfa(donnees.total_net)}</strong></span>
      </div>

      {donnees.cadeaux.length === 0 ? (
        <p className="mt-5 text-sm text-stone-500" data-testid="admin-cadeaux-vide">Aucun cadeau pour le moment.</p>
      ) : (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-stone-400">
                <th className="py-2 pr-4">Date</th>
                <th className="py-2 pr-4">Annonceur</th>
                <th className="py-2 pr-4">Montant</th>
                <th className="py-2 pr-4">Net</th>
                <th className="py-2 pr-4">Moyen</th>
                <th className="py-2 pr-4">Payeur</th>
                <th className="py-2 pr-4">Statut</th>
                <th className="py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {donnees.cadeaux.map((c) => (
                <tr key={c.id} className="border-t border-[#F0EBE3]" data-testid={`admin-cadeau-${c.id}`}>
                  <td className="py-2.5 pr-4 text-xs text-stone-500">
                    {new Date(c.created_at).toLocaleString("fr-FR")}
                  </td>
                  <td className="py-2.5 pr-4 font-semibold text-stone-800">{c.annonceur_nom}</td>
                  <td className="py-2.5 pr-4 text-stone-700">{fcfa(c.amount)}</td>
                  <td className="py-2.5 pr-4 text-stone-700">{fcfa(c.net)}</td>
                  <td className="py-2.5 pr-4 text-xs text-stone-500">{c.method}</td>
                  <td className="py-2.5 pr-4 text-xs text-stone-500">{c.payer_name || "—"} {c.payer_phone}</td>
                  <td className="py-2.5 pr-4">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                      c.status === "paid" ? "bg-[#0A5C36]/10 text-[#0A5C36]"
                        : c.status === "failed" ? "bg-[#E05638]/10 text-[#E05638]"
                        : "bg-stone-100 text-stone-500"}`}>
                      {c.status === "paid" ? "Payé" : c.status === "failed" ? "Échoué" : "En attente"}
                    </span>
                  </td>
                  <td className="py-2.5">
                    {c.reverse ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0A5C36]">
                        <CheckCircle2 size={13} /> Reversé
                      </span>
                    ) : c.status === "paid" ? (
                      <button onClick={() => reverser(c.id)} data-testid={`admin-cadeau-reverser-${c.id}`}
                        className="rounded-full bg-stone-900 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-stone-700">
                        Reversé
                      </button>
                    ) : (
                      <span className="text-xs text-stone-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default CadeauxAdmin;
